/** Row-major grid helpers. Every cell is an index `row * size + col`. */

import type { Board, CellIdx } from "./types";

export const rowOf = (cell: CellIdx, size: number): number => Math.floor(cell / size);
export const colOf = (cell: CellIdx, size: number): number => cell % size;
export const cellAt = (row: number, col: number, size: number): CellIdx => row * size + col;

export const inBounds = (row: number, col: number, size: number): boolean =>
  row >= 0 && row < size && col >= 0 && col < size;

/** The up-to-4 orthogonal neighbours of a cell. */
export function orthogonalNeighbours(cell: CellIdx, size: number): CellIdx[] {
  const row = rowOf(cell, size);
  const col = colOf(cell, size);
  const out: CellIdx[] = [];
  if (row > 0) out.push(cell - size);
  if (row < size - 1) out.push(cell + size);
  if (col > 0) out.push(cell - 1);
  if (col < size - 1) out.push(cell + 1);
  return out;
}

export const roomOf = (board: Board, cell: CellIdx): number => board.regions[cell] as number;

/**
 * "Beside" in Murdoku means orthogonally adjacent AND in the same room — a wall between two cells
 * breaks the relationship even though they touch.
 */
export function besideCells(board: Board, cell: CellIdx): CellIdx[] {
  const room = roomOf(board, cell);
  return orthogonalNeighbours(cell, board.size).filter((n) => roomOf(board, n) === room);
}

/**
 * A corner of a room: a cell with at most one orthogonal neighbour inside the same room, or one
 * where the in-room neighbours meet at a right angle (i.e. not a straight corridor).
 */
export function isRoomCorner(board: Board, cell: CellIdx): boolean {
  const neighbours = besideCells(board, cell);
  if (neighbours.length <= 1) return true;
  if (neighbours.length > 2) return false;
  const [a, b] = neighbours as [CellIdx, CellIdx];
  const sameRow = rowOf(a, board.size) === rowOf(b, board.size);
  const sameCol = colOf(a, board.size) === colOf(b, board.size);
  // Two neighbours in a straight line = a corridor cell, not a corner.
  return !sameRow && !sameCol;
}

/** Every cell of the grid, ascending. */
export function allCells(size: number): CellIdx[] {
  return Array.from({ length: size * size }, (_, i) => i);
}

/** True when every cell carrying `regionId` forms one orthogonally-connected blob. */
export function isRegionContiguous(regions: number[], size: number, regionId: number): boolean {
  const cells = regions.reduce<CellIdx[]>((acc, id, cell) => {
    if (id === regionId) acc.push(cell);
    return acc;
  }, []);
  if (cells.length === 0) return true;

  const seen = new Set<CellIdx>([cells[0] as CellIdx]);
  const queue: CellIdx[] = [cells[0] as CellIdx];
  while (queue.length > 0) {
    const cell = queue.pop() as CellIdx;
    for (const n of orthogonalNeighbours(cell, size)) {
      if (regions[n] === regionId && !seen.has(n)) {
        seen.add(n);
        queue.push(n);
      }
    }
  }
  return seen.size === cells.length;
}

/**
 * A column's name on the board, chessboard style: the first column is "a", the second "b".
 *
 * Deliberately LOWER case. Suspects are labelled with capitals (A, B, C…), and a board that
 * printed a capital B on its edge while a pencilled B sat in a square would be asking the player
 * to tell two different meanings of the same letter apart at a glance. Chess notation is lower
 * case for the same reason it works: files and pieces never look alike.
 */
export function columnLabel(col: number): string {
  let label = "";
  let index = col;
  while (index >= 0) {
    label = String.fromCharCode(97 + (index % 26)) + label;
    index = Math.floor(index / 26) - 1;
  }
  return label;
}
